import React, { useState, useEffect, useRef } from 'react';
import logo from '../logo_blade.png';
import { db } from '../services/firebase';
import { collection, getDocs } from 'firebase/firestore';

function distanciaKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function formatarDistancia(km) {
  if (km < 1) return `${Math.round(km * 1000)} m`;
  return `${km.toFixed(1).replace('.', ',')} km`;
}

function Agendamento({ onVerBarbeiros, onInicio }) {
  const [barbearias, setBarbearias] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState('');
  const [userCoords, setUserCoords] = useState(null);
  const [geoStatus, setGeoStatus] = useState('loading');
  const [favoritoId, setFavoritoId] = useState(() => localStorage.getItem('blade_favorito') || null);
  const watchIdRef = useRef(null);

  useEffect(() => {
    async function buscarBarbearias() {
      try {
        const snapshot = await getDocs(collection(db, 'barbearias'));
        const dados = snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(b => !b.status || b.status === 'aprovado');
        setBarbearias(dados);
      } catch (e) {
        console.error(e);
      }
      setCarregando(false);
    }
    buscarBarbearias();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!navigator.geolocation) { setGeoStatus('unavailable'); return; }
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        setUserCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGeoStatus('ok');
      },
      (err) => {
        // 1 = PERMISSION_DENIED, 2 = POSITION_UNAVAILABLE, 3 = TIMEOUT
        setGeoStatus(err.code === 1 ? 'denied' : 'unavailable');
      },
      { timeout: 8000, maximumAge: 30000 }
    );
    return () => {
      if (watchIdRef.current !== null) navigator.geolocation.clearWatch(watchIdRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function toggleFavorito(e, id) {
    e.stopPropagation();
    if (favoritoId === id) {
      localStorage.removeItem('blade_favorito');
      setFavoritoId(null);
    } else {
      localStorage.setItem('blade_favorito', id);
      setFavoritoId(id);
    }
  }

  const DIAS_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  function temHorarioHoje(barbeiro) {
    const agora = new Date();
    const agoraMins = agora.getHours() * 60 + agora.getMinutes();
    const diaSemana = DIAS_SEMANA[agora.getDay()];
    const hp = barbeiro?.horariosPorDia;
    if (hp) {
      const periodos = hp[diaSemana];
      if (!periodos || periodos.length === 0) return false;
      return periodos.some(p => {
        const [fh, fm] = p.fim.split(':').map(Number);
        return fh * 60 + fm > agoraMins;
      });
    }
    const diasTrabalho = barbeiro?.diasTrabalho || [];
    if (!diasTrabalho.includes(diaSemana)) return false;
    const [fh, fm] = (barbeiro?.horaFim || '19:00').split(':').map(Number);
    return fh * 60 + fm > agoraMins;
  }

  function temHorarioBarbeariaHoje(b) {
    const barbs = (b?.barbeiros || []).filter(x => !x.oculto);
    if (barbs.length === 0) return false;
    return barbs.some(x => temHorarioHoje(x));
  }

  function getMapsUrl(b) {
    const end = b.endereco?.completo || b.nome;
    return `https://maps.google.com/?q=${encodeURIComponent(end)}`;
  }

  function getWazeUrl(b) {
    const end = b.endereco?.completo || b.nome;
    return `https://waze.com/ul?q=${encodeURIComponent(end)}`;
  }

  function getEnderecoFormatado(b) {
    if (b.endereco?.rua) {
      return `${b.endereco.rua}, ${b.endereco.numero} — ${b.endereco.bairro}, ${b.endereco.cidade}`;
    }
    return b.endereco?.completo || '';
  }

  function calcularDistancia(b) {
    if (!userCoords || !b.lat || !b.lng) return null;
    return distanciaKm(userCoords.lat, userCoords.lng, b.lat, b.lng);
  }

  const barbeariaFavorita = favoritoId ? barbearias.find(b => b.id === favoritoId) : null;

  const barbeirasFiltradas = barbearias
    .filter(b => b.id !== favoritoId)
    .filter(b =>
      b.nome?.toLowerCase().includes(busca.toLowerCase()) ||
      b.endereco?.bairro?.toLowerCase().includes(busca.toLowerCase()) ||
      b.endereco?.cidade?.toLowerCase().includes(busca.toLowerCase())
    )
    .map(b => ({ ...b, _distancia: calcularDistancia(b) }))
    .sort((a, b) => {
      if (a._distancia === null && b._distancia === null) return 0;
      if (a._distancia === null) return 1;
      if (b._distancia === null) return -1;
      return a._distancia - b._distancia;
    });

  function CardBarbearia({ b, destaque = false }) {
    const dist = calcularDistancia(b);
    const isFav = favoritoId === b.id;
    return (
      <div
        onClick={() => onVerBarbeiros(b)}
        style={{
          backgroundColor: destaque ? '#131f13' : '#1a1a1a',
          border: destaque ? '1px solid #3d6b3d' : '1px solid #252525',
          borderRadius: '12px', padding: '12px', marginBottom: '10px', cursor: 'pointer',
        }}
      >
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
          {/* Foto */}
          <div style={{
            width: '72px', height: '72px', backgroundColor: '#2a2a2a',
            borderRadius: '8px', flexShrink: 0, overflow: 'hidden',
          }}>
            {b.foto
              ? <img src={b.foto} alt={b.nome} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => e.target.style.display = 'none'} />
              : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px' }}>✂️</div>
            }
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
              <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '3px' }}>{b.nome}</div>
              <button
                onClick={e => toggleFavorito(e, b.id)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 0 0 4px', fontSize: '18px', flexShrink: 0, lineHeight: 1 }}
              >
                {isFav ? '⭐' : '☆'}
              </button>
            </div>

            {destaque && (
              <div style={{ fontSize: '10px', color: '#7bc47b', letterSpacing: '0.5px', marginBottom: '4px' }}>
                ⭐ Sua barbearia favorita
              </div>
            )}

            {/* Endereço + distância */}
            <div style={{ fontSize: '11px', color: '#8f8f8f', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
              <span>📍 {getEnderecoFormatado(b)}</span>
              {dist !== null && (
                <span style={{ backgroundColor: '#1e2e1e', color: '#7bc47b', border: '0.5px solid #2d4a2d', borderRadius: '10px', padding: '1px 7px', fontSize: '10px', flexShrink: 0 }}>
                  {formatarDistancia(dist)}
                </span>
              )}
            </div>

            {/* Serviços — prévia */}
            {b.servicos?.length > 0 && (
              <div style={{ fontSize: '11px', color: '#9c9c9c', marginBottom: '6px' }}>
                {b.servicos.slice(0, 2).map(s => s.nome).join(' · ')}
                {b.servicos.length > 2 ? ` +${b.servicos.length - 2}` : ''}
              </div>
            )}

            {/* Badge de promoção — compatível com formato antigo e novo */}
            {(() => {
              const p = b.promocao;
              if (!p) return null;
              const nomesDias = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'];
              // Novo formato: sub-objetos por tipo
              const isNovoFormato = p.simples !== undefined || p.dias !== undefined || p.fidelidade !== undefined;
              if (isNovoFormato) {
                const badges = [];
                if (p.fidelidade?.ativa) {
                  const meta = p.fidelidade.fidelidadeMeta || 10;
                  badges.push(
                    <div key="fid" style={{ display: 'inline-block', fontSize: '10px', color: '#7bc47b', backgroundColor: '#141f14', border: '0.5px solid #2d4a2d', borderRadius: '10px', padding: '2px 8px', marginBottom: '4px', marginRight: '4px' }}>
                      ⭐ {p.fidelidade.texto || `Ganhe 1 grátis a cada ${meta} visitas`}
                    </div>
                  );
                }
                if (p.dias?.ativa && (p.dias.diasSemana || []).length > 0) {
                  const diasStr = p.dias.diasSemana.map(i => nomesDias[i]).join(', ');
                  badges.push(
                    <div key="dias" style={{ display: 'inline-block', fontSize: '10px', color: '#c9a84c', backgroundColor: '#2a1a05', border: '0.5px solid #c9a84c', borderRadius: '10px', padding: '2px 8px', marginBottom: '4px', marginRight: '4px' }}>
                      🏷️ {p.dias.texto || `${p.dias.desconto}% off — ${diasStr}`}
                    </div>
                  );
                }
                if (p.simples?.ativa && p.simples.desconto > 0) {
                  badges.push(
                    <div key="sim" style={{ display: 'inline-block', fontSize: '10px', color: '#c9a84c', backgroundColor: '#2a1a05', border: '0.5px solid #c9a84c', borderRadius: '10px', padding: '2px 8px', marginBottom: '4px', marginRight: '4px' }}>
                      🏷️ {p.simples.texto || `${p.simples.desconto}% off`}
                    </div>
                  );
                }
                return badges.length > 0 ? <div style={{ marginBottom: '2px' }}>{badges}</div> : null;
              }
              // Formato legado
              if (!p.ativa) return null;
              if (p.tipo === 'fidelidade') {
                const meta = p.fidelidadeMeta || 10;
                return (
                  <div style={{ display: 'inline-block', fontSize: '10px', color: '#7bc47b', backgroundColor: '#141f14', border: '0.5px solid #2d4a2d', borderRadius: '10px', padding: '2px 8px', marginBottom: '6px' }}>
                    ⭐ {p.texto || `Ganhe 1 grátis a cada ${meta} visitas`}
                  </div>
                );
              }
              if (p.tipo === 'dias' && (p.diasSemana || []).length > 0) {
                const diasStr = p.diasSemana.map(i => nomesDias[i]).join(', ');
                return (
                  <div style={{ display: 'inline-block', fontSize: '10px', color: '#c9a84c', backgroundColor: '#2a1a05', border: '0.5px solid #c9a84c', borderRadius: '10px', padding: '2px 8px', marginBottom: '6px' }}>
                    🏷️ {p.texto || `${p.desconto}% off — ${diasStr}`}
                  </div>
                );
              }
              return (
                <div style={{ display: 'inline-block', fontSize: '10px', color: '#c9a84c', backgroundColor: '#2a1a05', border: '0.5px solid #c9a84c', borderRadius: '10px', padding: '2px 8px', marginBottom: '6px' }}>
                  🏷️ {p.texto || (p.desconto > 0 ? `${p.desconto}% off` : 'Promoção ativa!')}
                </div>
              );
            })()}

            {/* Badge sem horário hoje */}
            {!temHorarioBarbeariaHoje(b) && (
              <div style={{ display: 'inline-block', fontSize: '10px', color: '#c9a84c', backgroundColor: '#2a2010', border: '0.5px solid #3a3010', borderRadius: '10px', padding: '2px 8px', marginBottom: '6px' }}>
                Sem horário hoje
              </div>
            )}

            {/* Botões Maps e Waze */}
            <div style={{ display: 'flex', gap: '6px' }}>
              <a href={getMapsUrl(b)} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                style={{ backgroundColor: '#1a1a2a', color: '#7bc4ff', border: '0.5px solid #2a2a4a', borderRadius: '16px', padding: '3px 10px', fontSize: '10px', textDecoration: 'none' }}>
                🗺 Maps
              </a>
              <a href={getWazeUrl(b)} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                style={{ backgroundColor: '#1a1a2a', color: '#7bc4ff', border: '0.5px solid #2a2a4a', borderRadius: '16px', padding: '3px 10px', fontSize: '10px', textDecoration: 'none' }}>
                🚗 Waze
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#0f0f0f', minHeight: '100vh', fontFamily: 'Arial, sans-serif', color: '#e8e8e8' }}>

      {/* Cabeçalho */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid #222', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <img src={logo} alt="BLADE" style={{ height: '40px', cursor: 'pointer' }} onClick={onInicio} />
      </div>

      <div style={{ padding: '24px 20px 0' }}>
        <h1 style={{ fontSize: '26px', fontWeight: '700', margin: '0 0 16px 0' }}>Agendar horário</h1>

        {/* Barbearia favorita */}
        {!carregando && barbeariaFavorita && (
          <div style={{ marginBottom: '8px' }}>
            <CardBarbearia b={barbeariaFavorita} destaque />
          </div>
        )}

        {/* Aviso de GPS negado */}
        {(geoStatus === 'denied' || geoStatus === 'unavailable') && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', backgroundColor: '#181818', border: '0.5px solid #2a2a2a', borderRadius: '10px', padding: '10px 12px', marginBottom: '12px' }}>
            <span style={{ fontSize: '14px', flexShrink: 0 }}>📍</span>
            <div>
              <div style={{ fontSize: '12px', color: '#aeaeae', lineHeight: '1.5' }}>
                {geoStatus === 'denied'
                  ? 'Localização desativada. As distâncias não serão exibidas.'
                  : 'Localização indisponível no momento.'}
              </div>
              <div style={{ fontSize: '11px', color: '#8f8f8f', marginTop: '2px' }}>
                Use a busca abaixo para encontrar por bairro ou cidade.
              </div>
            </div>
          </div>
        )}

        {/* Busca */}
        <input
          placeholder={geoStatus === 'denied' || geoStatus === 'unavailable' ? 'Buscar por nome, bairro ou cidade...' : 'Procurar barbearias...'}
          value={busca}
          onChange={e => setBusca(e.target.value)}
          style={{
            width: '100%', padding: '12px', borderRadius: '10px',
            border: '1px solid #2a2a2a', backgroundColor: '#1a1a1a',
            color: '#e8e8e8', fontSize: '14px', boxSizing: 'border-box',
            outline: 'none', fontFamily: 'Arial, sans-serif',
          }}
        />
      </div>

      {/* Lista */}
      <div style={{ padding: '16px 20px 32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <span style={{ fontWeight: '600', fontSize: '15px' }}>Barbearias disponíveis</span>
          {geoStatus === 'ok' && (
            <span style={{ fontSize: '10px', color: '#7bc47b', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span>📍</span> perto de você
            </span>
          )}
          {(geoStatus === 'denied' || geoStatus === 'unavailable') && (
            <span style={{ fontSize: '10px', color: '#8f8f8f' }}>📍 sem localização</span>
          )}
        </div>

        {carregando ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#8f8f8f', fontSize: '13px' }}>
            Carregando barbearias...
          </div>
        ) : barbeirasFiltradas.length === 0 && !barbeariaFavorita ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#8f8f8f', fontSize: '13px' }}>
            Nenhuma barbearia encontrada.
          </div>
        ) : (
          barbeirasFiltradas.map((b, i) => <CardBarbearia key={i} b={b} />)
        )}
      </div>
    </div>
  );
}

export default Agendamento;
